import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  musicProductDigitalInitialValues,
  musicProductDigitalTabs,
} from 'components/organisms/forms/music-product-digital/MusicProductDigitalForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiFloppyDisk, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetMusicProductDigital,
  usePutMusicProductDigital,
} from 'services/music-product-digital';
import {
  useDeleteMusicProductDigitalImage,
  usePostMusicProductDigitalImage,
  usePutMusicProductDigitalImage,
} from 'services/music-product-digital/image';
import {
  useDeleteMusicProductDigitalImageThumbnail,
  usePostMusicProductDigitalImageThumbnail,
  usePutMusicProductDigitalImageThumbnail,
} from 'services/music-product-digital/imageThumbnail';
import { usePostMusicProductDigitalSettlementInfo } from 'services/music-product-digital/settlementInfo';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastTabPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditMusicProductDigital = () => {
  const digitalMusicProductId = useParamId('music-product-digital');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const { data: musicProductDigital, isLoading } = useGetMusicProductDigital(
    digitalMusicProductId
  );
  const updateMusicProductDigital = usePutMusicProductDigital();

  const updateSettlementInfo = usePostMusicProductDigitalSettlementInfo();

  const addImage = usePostMusicProductDigitalImage();
  const changeImage = usePutMusicProductDigitalImage(digitalMusicProductId);
  const deleteImage = useDeleteMusicProductDigitalImage(digitalMusicProductId);
  const addImageThumbnail = usePostMusicProductDigitalImageThumbnail();
  const changeImageThumbnail = usePutMusicProductDigitalImageThumbnail(
    digitalMusicProductId
  );
  const deleteImageThumbnail = useDeleteMusicProductDigitalImageThumbnail(
    digitalMusicProductId
  );

  const mutateOptions = useMutateOptions<IMusicProductDigitalForm>();

  const musicProductDigitalEditInitialValues = musicProductDigital
    ? middleware.musicProductDigital.queryToForm(musicProductDigital)
    : musicProductDigitalInitialValues;

  const onEditMusicProductDigital = async (
    values: IMusicProductDigitalForm,
    formikHelpers: FormikHelpers<IMusicProductDigitalForm>
  ) => {
    if (values.file) {
      musicProductDigitalEditInitialValues.productImageLink?.length
        ? await changeImage.mutateAsync(values.file, {
            onError: error => {
              onErrorShowToast(error, 'toast-message.change-album-cover');
            },
          })
        : await addImage.mutateAsync(
            { file: values.file, id: digitalMusicProductId },
            {
              onError: error => {
                onErrorShowToast(error, 'toast-message.add-album-cover');
              },
              onSuccess: data =>
                formikHelpers.setFieldValue(
                  'productImageLink',
                  data.productImageLink
                ),
            }
          );
    }

    if (
      !!musicProductDigitalEditInitialValues.productImageLink?.length &&
      !values.productImageLink?.length &&
      !values.file
    ) {
      await deleteImage.mutateAsync(undefined, {
        onError: error => {
          onErrorShowToast(error, 'toast-message.remove-album-cover');
        },
      });
    }

    if (values.thumbnailFile) {
      musicProductDigitalEditInitialValues.thumbnailImageLink?.length
        ? await changeImageThumbnail.mutateAsync(values.thumbnailFile, {
            onError: error => {
              onErrorShowToast(error, 'toast-message.change-album-cover');
            },
          })
        : await addImageThumbnail.mutateAsync(
            { file: values.thumbnailFile, id: digitalMusicProductId },
            {
              onError: error => {
                onErrorShowToast(error, 'toast-message.add-album-cover');
              },
              onSuccess: data =>
                formikHelpers.setFieldValue(
                  'thumbnailImageLink',
                  data.productImageLink
                ),
            }
          );
    }

    if (
      !!musicProductDigitalEditInitialValues.thumbnailImageLink?.length &&
      !values.thumbnailImageLink?.length &&
      !values.thumbnailFile
    ) {
      await deleteImageThumbnail.mutateAsync(undefined, {
        onError: error => {
          onErrorShowToast(error, 'toast-message.remove-album-cover');
        },
      });
    }

    const musicProductDigitalMutation =
      middleware.musicProductDigital.formToQuery(values);

    await updateMusicProductDigital.mutateAsync(
      { ...musicProductDigitalMutation, digitalMusicProductId },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: goToIndividualMusicProductDigital,
      }
    );
  };

  const goToIndividualMusicProductDigital = () =>
    navigate(
      `/music-product-digital/${digitalMusicProductId}/${getLastTabPathname(
        pathname,
        4
      )}`
    );

  return (
    <Form.MusicProductDigital
      i18nextPrefix='music-product-digital'
      onSubmit={onEditMusicProductDigital}
      initialValues={musicProductDigitalEditInitialValues}
      disableTabs={[
        musicProductDigitalTabs.delivery,
        musicProductDigitalTabs.statistics,
        musicProductDigitalTabs.smartlink,
        musicProductDigitalTabs.assetIds,
      ]}
      status={{ isLoading }}
      customBackItem={
        <Button
          type='button'
          onlyIcon
          onClick={goToIndividualMusicProductDigital}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <div className='flex items-center gap-2'>
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text={t('button.save')} />
            <Button.Icon icon={<PiFloppyDisk />} />
          </Button>
        </div>
      }
    />
  );
};

export default EditMusicProductDigital;
